import './ModuleItem.scss';
import '../../../Tabs.scss';

import useSetPageTitle from 'utilities/useSetPageTitle';
import { AdminModuleTabs, AdminModulesV2RelativeRoutes, AdminPagesRelativeRoutes } from 'Routes';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useChaptersStore } from 'stores/Admin/Editors/ModuleEditorV2/ChaptersStore';
import { useGeneralStore } from 'stores/Admin/Editors/ModuleEditorV2/GeneralStore';
import { useCertificationsStore } from 'stores/Admin/Editors/ModuleEditorV2/CertificationsStore';
import { Button, Tab, Tabs } from '@trackman/web-shared-components';
import { AdminModule } from 'types/admin/modulesV2';
import Box from '@mui/material/Box/Box';
import { useTabsStore } from 'stores/TabsStore';
import { useAdminModuleV2 } from 'hooks/Admin/ModulesV2/useAdminModuleV2';
import { useCreateModuleV2 } from 'hooks/Admin/ModulesV2/useCreateModuleV2';
import { useUpdateModuleV2 } from 'hooks/Admin/ModulesV2/useUpdateModuleV2';
import { useSendModuleToTranslationV2 } from 'hooks/Admin/ModulesV2/useSendModuleToTranslationV2';
import { TranslationStatusChip } from '../TranslationStatusChip/TranslationStatusChip';

export const ModuleItem = () => {
  const { activeTabIndex } = useTabsStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const moduleId = useParams().moduleId ?? '';
  const { data, isPending } = useAdminModuleV2(moduleId);

  const setInitialChapters = useChaptersStore((state) => state.setInitialChapters);
  const setInitialGeneralData = useGeneralStore((state) => state.setInitialData);
  const setInitialCertifications = useCertificationsStore((state) => state.setInitialCertifications);
  const resetChapters = useChaptersStore((state) => state.reset);
  const resetGeneralData = useGeneralStore((state) => state.reset);
  const resetCertifications = useCertificationsStore((state) => state.reset);
  const setInitializingGeneral = useGeneralStore((state) => state.setLoadingInitialData);
  const setInitializingChapters = useChaptersStore((state) => state.setLoadingInitialData);
  const setInitializingCertifications = useCertificationsStore((state) => state.setLoadingInitialData);
  const setLoadingGeneral = useGeneralStore((state) => state.setLoading);
  const setLoadingChapters = useChaptersStore((state) => state.setLoading);
  const setLoadingCertifications = useCertificationsStore((state) => state.setLoading);
  const loading = useGeneralStore((state) => state.loading);
  const title = useGeneralStore((state) => state.title);
  const shortDescription = useGeneralStore((state) => state.shortDescription);

  const translationStatus = data?.translationProgress?.status;

  useSetPageTitle({ title: data?.content.title ?? title });

  const saveModulePayload: Omit<AdminModule, 'id'> = {
    content: {
      id: useGeneralStore((state) => state.id),
      title: useGeneralStore((state) => state.title),
      shortDescription: useGeneralStore((state) => state.shortDescription),
      fullDescription: useGeneralStore((state) => state.fullDescription),
      imageId: useGeneralStore((state) => state.imageId),
      status: useGeneralStore((state) => state.status),
      chapters: useChaptersStore((state) => state.chapters),
      certifications: useCertificationsStore((state) => state.certifications),
    },
    isOrganizationOnly: useGeneralStore((state) => state.isOrganizationOnly),
    allowedOrganizationIds: useGeneralStore((state) => state.allowedOrganizationIds),
    localizedImages: useChaptersStore((state) => state.localizedImages),
  };

  const onMutate = () => {
    setLoadingGeneral(true);
    setLoadingChapters(true);
    setLoadingCertifications(true);
  };
  const onSettled = () => {
    setLoadingGeneral(false);
    setLoadingChapters(false);
    setLoadingCertifications(false);
  };
  const invalidateModuleLists = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['adminModuleListV2'] }),
      queryClient.invalidateQueries({ queryKey: ['learningHubModuleList'] }),
    ]);
  };

  const createModule = useCreateModuleV2({
    onMutate,
    onSuccess: async (id) => {
      navigate(`/Admin/${AdminPagesRelativeRoutes.ModulesV2}/${AdminModulesV2RelativeRoutes.ModuleItem}/${id}`);
      await invalidateModuleLists();
    },
    onSettled,
  });

  const updateModule = useUpdateModuleV2(moduleId, {
    onMutate,
    onSuccess: async () => {
      await Promise.all([
        invalidateModuleLists(),
        queryClient.invalidateQueries({ queryKey: ['adminModuleV2', moduleId] }),
      ]);
    },
    onSettled,
  });

  const sendToTranslation = useSendModuleToTranslationV2(moduleId);

  const navigateToModuleList = async () => {
    await queryClient.invalidateQueries({ queryKey: ['adminModuleListV2'] });
    navigate(`/Admin/${AdminPagesRelativeRoutes.ModulesV2}`);
  };

  useEffect(() => {
    setInitializingGeneral(isPending && !!moduleId);
    setInitializingChapters(isPending && !!moduleId);
    setInitializingCertifications(isPending && !!moduleId);
    if (!isPending && data) {
      setInitialChapters(data.content.chapters ?? [], data.localizedImages);
      setInitialCertifications(data.content.certifications ?? []);
      setInitialGeneralData({
        id: data.content.id,
        title: data.content.title,
        shortDescription: data.content.shortDescription,
        fullDescription: data.content.fullDescription,
        imageId: data.content.imageId,
        status: data.content.status,
        isOrganizationOnly: data.isOrganizationOnly ?? false,
        allowedOrganizationIds: data.allowedOrganizationIds ?? [],
      });
    }
  }, [isPending, data]);

  useEffect(
    () => () => {
      resetCertifications();
      resetChapters();
      resetGeneralData();
    },
    []
  );

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>{data?.content.title ?? title}</h1>
        <span className='page-subtitle'>{data?.content.shortDescription ?? shortDescription}</span>
      </div>

      <Box className='tabs-container'>
        <Tabs scrollButtons='auto' value={activeTabIndex} variant='scrollable' indicatorColor='primary' allowScrollButtonsMobile>
          <Tab label='General' onClick={() => navigate(AdminModuleTabs.EditGeneral)} />
          <Tab label='Chapters' onClick={() => navigate(AdminModuleTabs.EditChapters)} />
          <Tab label='Certifications' onClick={() => navigate(AdminModuleTabs.EditCertifications)} />
        </Tabs>
        <div className='buttons'>
          {moduleId && <TranslationStatusChip status={translationStatus} />}
          {moduleId && (
            <Button
              variant='contained'
              intent='secondary'
              onClick={() => sendToTranslation.mutate()}
              disabled={translationStatus === 'InTranslation' || loading || sendToTranslation.isPending}
              isLoading={sendToTranslation.isPending}
            >
              Send to translation
            </Button>
          )}
          <Button variant='contained' intent='tertiary' onClick={() => navigateToModuleList()} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant='contained'
            intent='primary'
            onClick={() => {
              if (moduleId) {
                updateModule.mutate({ ...saveModulePayload, id: moduleId });
              } else {
                const newId = crypto.randomUUID();
                createModule.mutate({ ...saveModulePayload, id: newId, content: { ...saveModulePayload.content, id: newId } });
              }
            }}
            disabled={loading}
            isLoading={loading}
          >
            Save
          </Button>
        </div>
      </Box>
      <Outlet />
    </>
  );
};
